import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Space } from 'antd';
import ReactCodeInput from 'react-verification-code-input';
import CloseAlerts from 'Components/alerts/assets/svg/CloseAlerts';
import {
  primaryColor, primaryHeadingColor, secondaryFont, wBold,
} from 'Utils/utilsStyle';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'Slice/SignUpSlice';
import {
  selectVerificationCodeLoaded, selectVerificationCodeData,
  selectVerificationCodeLoading, selectVerificationCodeEmailLoading,
} from 'Selector/SignUpSelector';
import InfoIconTwoRed from 'Components/common/assets/svgIcons/InfoIconTwoRed';
import { selectIsSamsung } from 'Selector/LoginSelector';
import { otherUsersCount, samsungCount } from 'Utils/Constants';
import Email from '../assets/img/Email.svg';
import ButtonLite from './ButtonLite';

const StyleWrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  height: 100vh;
  width: 100%;
  display: flex;
  top: 0;
  justify-content: center;
  align-items: center;
  position: fixed;
  left:0px;
  z-index: 100;
  .fancy-box {
    position:relative;
    width: 448px;
    max-width: 100%;
    border-radius: 22px;
    box-shadow: 0 0 60px rgba(0, 0, 0, 0.14);
    background: rgb(255, 255, 255);
    padding: 70px 50px 55px 50px;
    .description {
      font-family: Roboto, sans-serif;
      color: #5e7079;
      line-height: 16px;
      font-size: 12px;
      margin: 30px 0px !important;
    }
    .title-div {
      color: #424b50;
      margin: 0;
      padding: 0;
      font-family: "Roboto Slab";
      font-weight: bold;
      font-size: 35px;
      line-height:35px;
      span {
        color: #1ec252;
        display:block;
      }
    }
    .img-div{
      img {
        width: 60px;
        margin-bottom: 25px;
      }
    }
    .action-div {
      margin-top: 34px;
    }
  }
  .validation-input{
    display: flex;
    justify-content: center;
    width: 100% !important;
    input{
      border-color: ${primaryColor};
      caret-color: ${primaryColor};
      font-weight: ${wBold};
      color: ${primaryHeadingColor};
      font-family: ${secondaryFont};
      margin: 0px 5px;
      border: 2px solid ${primaryColor};
      border-radius: 5px;
      width: 50px !important;
      padding: 3px !important;
      border-left: 2px solid ${primaryColor} !important;
      border-right: 2px solid ${primaryColor} !important;
      &:focus {
        border: 2px solid ${primaryColor};
      }
    }
  }
  .expiry-text{
    color: #5E7079;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.444444px;
    display: flex;
    width: 100%;
    justify-content: center;
    padding-top: 39px;
    font-family: 'Roboto';
  }
  .green{
    color: #1EC252;
  }
  .disabled{
    pointer-events :none;
    opacity: 0.7;
  }
  .padded-text{
    padding-left: 3px;
    padding-right: 3px;
  }
  .crossIcon{
    position: absolute;
    top: 5%;
    right:5%;
  }
  .pointer{
    cursor:pointer;
  }
  .invalid-code{
color: #E02020;
    font-family: 'Roboto';
    font-weight: 400;
    font-size: 16px;
    /* line-height: 18px; */
    /* margin-top: 17px; */
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: center;
    padding-top: 41px;
    /* margin-top: 41px; */
}
  .pt-5{
    padding-top: 5px;
  }  
  .font-14{
    font-size: 14px !important;
  }
  }
  .m-r-5{
    margin-right : 5px;
  }
  .text-width{
      width: 382px;
  }
`;

type Props = {
  username?: string
  onVerificationCodeSuccess: () => void
  onClosePopup: () => void
};

const defaultProps = {
  username: '',
};

const ValidationCodePopup = function ValidationCodePopup(
  {
    username,
    onVerificationCodeSuccess,
    onClosePopup,
  }: Props,
) {
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const [code, setCode] = useState('');
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  const isSamsung = useSelector(selectIsSamsung);
  const [seconds, setSeconds] = useState<number>(
    isSamsung === 1 ? samsungCount : otherUsersCount,
  );
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const [intervalId, setIntervalId] = useState<number>();
  const [maximumTriesDone, setMaximumTriesDone] = useState(false);

  const loaded = useSelector(selectVerificationCodeLoaded);
  const response = useSelector(selectVerificationCodeData);
  const loading = useSelector(selectVerificationCodeLoading);
  const emailSending = useSelector(selectVerificationCodeEmailLoading);
  const dispatch = useDispatch();

  const submitCode = () => {
    const obj = {
      username,
      code,
      appName: 'xiq-webapp',
    };
    dispatch(actions.submitVerificationCode(obj));
  };

  const countDownTimer = () => {
    if (intervalId) clearInterval(intervalId);
    const interval = setInterval(() => {
      setSeconds((secondsLapsed) => {
        if (secondsLapsed === 0) {
          clearInterval(interval);
          setIsTimerExpired(true);
          setMaximumTriesDone(false);
          setIsCodeInvalid(false);
          return 0;
        }
        return secondsLapsed - 1;
      });
    }, 1000);
    setIntervalId(interval as unknown as number);
  };

  useEffect(() => {
    if (isTimerExpired === true) {
      setSeconds(isSamsung === 1 ? samsungCount : otherUsersCount);
    }
    return () => {
      dispatch(actions.resetCodeSubmission());
    };
  }, [isTimerExpired]);

  const sendVerificationCodeEmail = () => {
    setIsTimerExpired(false);
    setIsCodeInvalid(false);
    const obj = {
      username,
    };
    dispatch(actions.sendVerificationCodeEmail(obj));
    countDownTimer();
  };

  useEffect(() => {
    if (loaded === true && response && response.status === true) {
      onVerificationCodeSuccess();
    } else if (loaded === true && response && response.status === false) {
      if (response.response.isButtonDisabled === true) {
        setMaximumTriesDone(true);
        setSeconds(isSamsung === 1 ? samsungCount : otherUsersCount);
      }
      setIsCodeInvalid(true);
      // setIsTimerExpired(true);
      // clearInterval(intervalId as number);
    }
  }, [loaded, response]);

  useEffect(() => {
    countDownTimer();
  }, []);

  const InvalidCode = (

    <span className="invalid-code pt-5 d-flex align-items-center">
      <InfoIconTwoRed className="m-r-5" />
      Incorrect code. Please try again.
    </span>
  );
  const ExpiredCode = (
    <span className="expiry-text">
      Your code has expired.
      {' '}
      <span className="green padded-text">Resend</span>
      {' '}
      verification code.
    </span>
  );
  const ElapsedTime = (
    <span className="expiry-text">
      Code expires in
      <b className="padded-text">
        {' '}
        {seconds}
        {' '}
      </b>
      {' '}
      seconds
    </span>
  );
  const MaximumTriesTime = (
    <span className="invalid-code line-block font-14">
      Too many failed attempts. Try again after
      <b className="padded-text">
        {seconds}
      </b>
      {' '}
      seconds
    </span>
  );

  const isSubmitDisabled = () => {
    if (loading || !isCodeComplete || isTimerExpired || maximumTriesDone) {
      return true;
    }
    return false;
  };

  return (
    <StyleWrapper>
      <div
        className="fancy-box"
        onClick={(e) => e.stopPropagation()}
        onKeyPress={(e) => e.stopPropagation()}
        role="button"
        aria-label="Close"
        tabIndex={0}
      >
        <CloseAlerts
          className="crossIcon pointer"
          onClick={() => {
            setIsTimerExpired(false);
            setIsCodeInvalid(false);
            setMaximumTriesDone(false);
            onClosePopup();
            dispatch(actions.resetCodeSubmission());
          }}
        />
        <div className="img-div mt-5">
          <img src={Email} alt="email" />
        </div>
        <h4 className="title-div mt-3 mb-3">
          You’re almost there.
          <span className="text-width">Enter Verification code</span>
        </h4>
        <p className="description mb-5">
          A verification code has been sent to your provided email and phone number.
        </p>
        <ReactCodeInput
          className="validation-input"
          fields={5}
          onComplete={(input: string) => { setCode(input); }}
          onChange={(input: string) => { setIsCodeComplete(input.length === 5); }}
        />
        {
          !maximumTriesDone && (isTimerExpired
            ? (
              ExpiredCode
            )
            : (
              ElapsedTime
            ))
        }
        {
          (isCodeInvalid && !maximumTriesDone)
          && (InvalidCode)
        }
        {
          maximumTriesDone
          && (MaximumTriesTime)
        }

        <div className="action-div pt-4 mb-3">
          <Space>
            <ButtonLite
              onClickButton={sendVerificationCodeEmail}
              title={emailSending ? 'Resending Code' : 'Resend Code'}
              outline
              className={`${(!isTimerExpired || emailSending) ? 'disabled' : ''}`}
            />
            <ButtonLite
              onClickButton={submitCode}
              title={`${loading ? 'Submitting' : 'Submit'}`}
              outline={false}
              className={`${isSubmitDisabled() ? 'disabled' : ''}`}
            />
          </Space>
        </div>
      </div>
    </StyleWrapper>
  );
};
ValidationCodePopup.defaultProps = defaultProps;
export default ValidationCodePopup;
